import type { EnumLiteralCreateOperation } from '@festina/configuration-general-types/configuration'
import { assertNotEmpty } from '@festina-life/core'
import { useCallback } from 'react'
import { useNavigate } from 'react-router'

import { configurationItem } from '../../system/paths'
import type { EnumDefinition } from '../../uiModels/enumLiterals/types'
import { useOperationsContext } from '../../workspace/operationsContext'
import { toEnumLiteralCreateOperation } from './helpers'

export const useUpdateEnumDefinition = (currentValue: EnumDefinition) => {
  const { onAddOrUpdateOperation, workspaceKey } = useOperationsContext()
  const navigate = useNavigate()

  return useCallback(
    async (nextDefinition: EnumDefinition) => {
      const allUpdates: ReturnType<typeof onAddOrUpdateOperation>[] = []

      if (nextDefinition.internalOperation) {
        allUpdates.push(
          onAddOrUpdateOperation({
            operation: nextDefinition.internalOperation,
            layer: nextDefinition.layer,
            block: nextDefinition.block,
            shouldOpenUnifiedBranchWorkspace: true,
          }),
        )

        const nextName = nextDefinition.internalOperation.entity?.name
        assertNotEmpty(nextName)

        const didNameChange =
          nextName !== currentValue.internalOperation?.entity?.name
        const didDeprecatedChange =
          nextDefinition.internalOperation.entity?.deprecated !==
          currentValue.internalOperation?.entity?.deprecated
        const didOrdinalChange =
          nextDefinition.internalOperation.entity?.ordinal !==
          currentValue.internalOperation?.entity?.ordinal

        const didRemoveOrdinal =
          didOrdinalChange && !nextDefinition.internalOperation.entity?.ordinal
        const shouldLiteralsBeUpdated =
          didNameChange || didRemoveOrdinal || didDeprecatedChange

        if (shouldLiteralsBeUpdated) {
          nextDefinition.literals.forEach((literal) => {
            const updatedLiteralOperation =
              literal.type === 'configured'
                ? literal.internalOperation
                : toEnumLiteralCreateOperation(literal)

            const enumLiteralOperation = {
              ...updatedLiteralOperation,
              entity: {
                _type: 'EnumLiteralInOut',
                ...updatedLiteralOperation.entity,
                enumName: nextName,
                ...(nextDefinition.internalOperation?.entity?.deprecated
                  ? { deprecated: true }
                  : {}),
                ...(didRemoveOrdinal ? { ordinal: undefined } : {}),
              },
            } satisfies EnumLiteralCreateOperation
            allUpdates.push(
              onAddOrUpdateOperation({
                operation: enumLiteralOperation,
                layer: nextDefinition.layer,
                block: nextDefinition.block,
                shouldOpenUnifiedBranchWorkspace: true,
              }),
            )
          })
        }

        await Promise.all(allUpdates)

        if (didNameChange && workspaceKey) {
          await navigate(
            configurationItem({
              workspaceName: workspaceKey.name,
              server: workspaceKey.branchKey.server,
              itemId: nextName,
              layer: nextDefinition.layer,
              block: nextDefinition.block,
              branch: workspaceKey.branchKey.name,
              type: 'enumDefinition',
            }),
          )
        }
      }
    },
    [
      currentValue.internalOperation?.entity?.deprecated,
      currentValue.internalOperation?.entity?.name,
      currentValue.internalOperation?.entity?.ordinal,
      navigate,
      onAddOrUpdateOperation,
      workspaceKey,
    ],
  )
}
